import { useState, useEffect } from "react";
import CVData from "../types/CVData";
import { getAllCVs, saveCVData } from "../services/cvService";

const PAGE_SIZE = 20;

export function useCVs() {
  const [cvs, setCVs] = useState<CVData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCVs = async (progressive = true) => {
    setLoading(true);
    setError(null);
    try {
      if (progressive) {
        const data = await getAllCVs({ limit: PAGE_SIZE });
        const items = Array.isArray(data) ? data : (data as any)?.items ?? [];
        const nextLastKey = Array.isArray(data)
          ? null
          : (data as any)?.nextLastKey ?? null;
        setCVs(items);
        setLoading(false);
        if (nextLastKey) {
          setLoadingMore(true);
          let all = [...items];
          let lastKey: string | null = nextLastKey;
          while (lastKey) {
            const nextData = await getAllCVs({
              limit: PAGE_SIZE,
              lastKey,
            });
            const nextItems = Array.isArray(nextData)
              ? nextData
              : (nextData as any)?.items ?? [];
            const nextKey = Array.isArray(nextData)
              ? null
              : (nextData as any)?.nextLastKey ?? null;
            all = [...all, ...nextItems];
            lastKey = nextKey;
            setCVs(all);
          }
          setLoadingMore(false);
        } else if (items.length === 0) {
          // Fallback: backend may not support pagination yet, fetch full list
          const fullData = await getAllCVs();
          setCVs(Array.isArray(fullData) ? fullData : (fullData as any)?.items ?? []);
        }
      } else {
        const data = await getAllCVs();
        setCVs(Array.isArray(data) ? data : (data as any)?.items ?? []);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch CVs");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const saveCV = async (cv: CVData) => {
    const result = await saveCVData(cv);
    await fetchCVs(); // Refresh CVs after saving
    return result;
  };

  useEffect(() => {
    fetchCVs();
  }, []);

  return { cvs, loading, loadingMore, error, saveCV, fetchCVs };
}
